import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const chainElements = [
  { label: '评价结论', icon: 'ri-medal-line' },
  { label: '维度', icon: 'ri-bar-chart-2-line' },
  { label: '证据卡', icon: 'ri-file-text-line' },
  { label: '课堂原话', icon: 'ri-mic-line' },
  { label: '时间戳', icon: 'ri-timer-line' },
  { label: '视频片段', icon: 'ri-video-line' },
  { label: '资料来源', icon: 'ri-folder-shield-line' },
];

const ledgerStatuses = [
  { label: '成立', color: 'bg-green-50 text-green-700 border-green-200' },
  { label: '存疑', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  { label: '无法核验', color: 'bg-rose-50 text-rose-700 border-rose-200' },
  { label: '待教研核实', color: 'bg-slate-50 text-foreground-950 border-slate-200' },
];

const verifyHistory = [
  {
    tag: '编造',
    tagColor: 'bg-rose-100 text-rose-700 border-rose-200',
    time: '2026-06-29 14:25:54',
    content: '人工审核确认原始编造风险，按编造处理',
    expiry: '2026-12-31 23:59:59',
    source: 'gold',
  },
  {
    tag: '口径误差',
    tagColor: 'bg-amber-100 text-amber-700 border-amber-200',
    time: '2026-06-27 16:10:01',
    content: '知识库显示阿克苏地区森林覆盖率从80年代初3.35%升至9.08%，权威信源均在9%左右，远未达70%。"70%"违背地理常识（当地沙漠、戈壁、山地占61.99%），属明显数据夸大。',
    expiry: '2026-12-31 23:59:59',
    source: 'needs_review',
  },
];

export default function P39EvidenceChain({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* 三层背景光效 */}
      <div ref={orb1Ref} className="absolute top-20 right-24 w-[380px] h-[380px] rounded-full bg-primary-100/14 blur-[130px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-[400px] h-[400px] rounded-full bg-accent-100/12 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />
      <div className="absolute top-1/2 left-1/3 w-[300px] h-[300px] rounded-full bg-secondary-200/20 blur-[140px] pointer-events-none animate-orb-float-3" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-5 md:py-6">
        <div ref={titleRef} className="text-center mb-3 md:mb-4">
          <span className={`inline-block text-base font-body font-semibold text-accent-600 uppercase tracking-[0.15em] mb-2 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>可追溯性</span>
          <h2 className={`font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground-950 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            证据链与<span className="bg-gradient-to-r from-primary-500 to-accent-500 bg-clip-text text-transparent">事实账本</span>
          </h2>
          <p className={`text-lg md:text-xl text-foreground-950 font-body mt-3 transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>每个结论都能被追问</p>
        </div>

        {/* 证据链流程 */}
        <div className={`max-w-6xl mx-auto mb-3 md:mb-4 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="flex flex-wrap items-center justify-center gap-x-1.5 md:gap-x-2 gap-y-1.5">
            {chainElements.map((el, i) => (
              <div key={el.label} className="flex items-center">
                <div className="flex items-center gap-1 px-3 py-2 md:px-4 md:py-2.5 rounded-lg glass-liquid border-primary-200/30">
                  <span className="font-mono text-base font-bold text-primary-400">{i + 1}</span>
                  <i className={`${el.icon} text-lg text-primary-500`} />
                  <span className="text-base md:text-lg font-body font-semibold text-foreground-950 whitespace-nowrap">{el.label}</span>
                </div>
                {i < chainElements.length - 1 && (
                  <i className="ri-arrow-right-s-line text-foreground-950 text-base mx-1" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 两栏：账本状态 + 实例 */}
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 max-w-6xl mx-auto mb-3 md:mb-4 transition-all duration-1000 delay-400 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {/* 四种状态 */}
          <div className="p-3.5 md:p-4 rounded-xl glass-liquid border-primary-200/30">
            <div className="relative z-10">
              <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-3">事实账本状态</h3>
              <div className="grid grid-cols-2 gap-2">
                {ledgerStatuses.map((s) => (
                  <div key={s.label} className={`px-3 py-2 md:px-3.5 md:py-2.5 rounded-lg border text-center ${s.color}`}>
                    <span className="text-base md:text-lg font-body font-semibold">{s.label}</span>
                  </div>
                ))}
              </div>
              <p className="text-base text-foreground-950 font-body mt-3 leading-relaxed">
                存疑≠教师讲错，而是资料口径和来源需进一步确认
              </p>
            </div>
          </div>

          {/* 核验历史 */}
          <div className="p-3.5 md:p-4 rounded-xl glass-liquid-gold border-accent-200/30">
            <div className="relative z-10">
              <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-3 flex items-center gap-2">
                <i className="ri-history-line text-accent-500" />
                核验历史
              </h3>
              <div className="relative pl-6 space-y-2.5">
                {/* 时间线竖线 */}
                <div className="absolute left-[10px] top-1.5 bottom-1.5 w-px bg-accent-200/60" />
                {verifyHistory.map((item, i) => (
                  <div key={i} className="relative">
                    {/* 时间线圆点 */}
                    <div className="absolute -left-6 top-1.5 w-5 h-5 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-accent-500 border-2 border-accent-300" />
                    </div>
                    {/* 卡片内容 */}
                    <div className="p-2.5 rounded-lg bg-background-50/70 border border-background-200/50">
                      <span className={`inline-block px-2 py-0.5 rounded-md border text-xs font-body font-semibold mb-1.5 ${item.tagColor}`}>
                        {item.tag}
                      </span>
                      <p className="text-sm text-foreground-950 font-body leading-snug">
                        {item.content}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* 底部 */}
        <div className={`max-w-3xl mx-auto text-center transition-all duration-1000 delay-600 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="inline-flex items-center gap-2 px-5 py-3 rounded-lg glass border-white/50">
            <i className="ri-loop-left-line text-primary-500 text-lg flex-shrink-0" />
            <span className="text-base md:text-lg font-body text-foreground-950 leading-relaxed">
              可追问、可反驳、可修订——比"模型永远正确"更有价值
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}